import { ILogState } from "source/app/presentation/MainPanel/Log/ILogState";
import { GetSignificanceSuggestions } from "source/app/presentation/MainPanel/Log/LogInputForm/EntrySignificance/LogInputEntrySignificance";
import { OrchestrateLogEntryPopup } from "source/app/presentation/MainPanel/Log/MultiplyKips/LogEntryPopup/OrchestrateLogEntryPopup";
import { WithAnimatedProgressBarAsync } from "../../presentation/Header/ProgressBar/LevelProgressBar/WithAnimatedProgressBar";
import { ILogInputFormState } from "../../presentation/MainPanel/Log/LogInputForm/LogInputForm";
import VerbConstants from "motw_core/Constants/VerbConstants";
import { GetLogEntrySound } from "../Sound/Helpers/GetLogEntrySound";
import { ESharedComponent } from "../UI/State/ESharedComponent";
import { State } from "../UI/State/State";
import {
    SaveLogEntryAsync,
    GetEntryPrefix,
} from "motw_core/Repositories/LogEntryRepository";
import { ValidateEntry } from "./ValidateEntry/ValidateEntry";
import moment = require("moment");
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import Data from "../Data";
import { ClearInputForms } from "../UI/ClearInputForms";
import Background from "../UI/Background/Background";
import { Sounds } from "../Sound/Sounds";
import {
    IsAGoal,
    GoalValueIsAllowed,
} from "source/app/presentation/MainPanel/Log/LogInputForm/LogInputFormHelpers";
import { DisplayToastrError } from "../../presentation/Shared/HandleError";
import { IGoalCardsState } from "../../presentation/MainPanel/Goals/GoalCards/Helpers/IGoalCardsState";
import { IGoalsContainerState } from "source/app/presentation/MainPanel/Goals/IGoalsState";
import { DeepWorkKipsTodayState } from "../../presentation/Header/Tools/DeepWorkKipsToday/DeepWorkKipsToday";
import { IGoal } from "../../../../../motw_core/Entity/IGoal";
import { StopStopWatch } from "source/app/presentation/Header/Center/Stopwatch/Stop/StopStopWatch";
import HowlAudioProvider from "../Sound/AudioProvider/HowlAudioProvider";

export function AddNewEntry(logState: ILogState) {
    if (!IsEntryValid(logState.LogInpEntryText)) {
        return;
    }

    WithAnimatedProgressBarAsync(() => AddLogEntryAsync(logState));
}

function IsEntryValid(entryText: string) {
    if (entryText === "") {
        DisplayToastrError("Entry text cannot be empty", "Whoops");
        return false;
    }

    const enteredGoalName = entryText.split(" ")[0]!;

    if (IsASingleWord(entryText) && ContainsANumber(enteredGoalName)) {
        DisplayToastrError("Goal name shouldn't contain a number", "Whoops");
        return false;
    }

    if (IsAGoal(enteredGoalName)) {
        if (!GoalValueIsAllowed(entryText, true)) {
            DisplayToastrError("Goal value is missing or invalid", "Whoops");
            return false;
        }

        const goal = State.ReadClass<IGoalsContainerState>(
            ESharedComponent.GoalsContainer
        ).state.Goals.find((g) => g.name === enteredGoalName)!;

        const goalNamesLoggedToday = State.ReadClass<IGoalCardsState>(
            ESharedComponent.GoalCards
        ).state.goalNamesLoggedToday;

        if (goal.is1time && goalNamesLoggedToday.includes(enteredGoalName)) {
            DisplayToastrError(
                "This goal has already been logged today",
                "Whoops"
            );

            return false;
        }

        if (IsGoalLimitBreached(goal)) {
            DisplayToastrError(
                "This goal is at the maximum allowed limit and cannot be logged",
                "Whoops"
            );

            return false;
        }
    }

    return true;
}

async function AddLogEntryAsync(logState: ILogState) {
    Sounds.kickdrum_heavy.Play();

    const inputValues = GetLogEntry(logState);
    const isValid = ValidateEntry(inputValues);

    if (isValid) {
        OrchestrateLogEntryPopup(inputValues);

        await SaveLogEntryAsync(
            inputValues,
            ClientDataProvider,
            false,
            OnErrorAddLogEntry,
            OnFinishAddLogEntry
        );

        StopStopWatch();

        var logEntrySoundFile = GetLogEntrySound();
        logEntrySoundFile.FadeOut(0, 1000);
    }
}

function GetLogEntry(logState: ILogState) {
    const entryVerb = logState.inpVerb;
    const entrySymbol = VerbConstants.GetSymbol(entryVerb);
    const entrySignificance = logState.inpSignificance;

    const entrySignificanceIndex = GetSignificanceSuggestions().findIndex(
        (elem) => elem === entrySignificance
    );

    const entryPrefix = GetEntryPrefix(entrySignificanceIndex, entrySymbol);

    return [
        entryPrefix + " " + entryVerb + " " + logState.LogInpEntryText,
        logState.inpKips,
        "9", // Default mood value - it's not being used
        moment().format("MMM D YYYY HH:mm"),
        "1", // Set energy value to default because its not being used
    ];
}

export function OnErrorAddLogEntry() {
    Sounds.kickdrum_heavy.Play();
}

export function OnFinishAddLogEntry() {
    Data.Load.AllAsync();
    ClearInputForms();
    Background.ChangeImage();
}

function IsGoalLimitBreached(goal: IGoal) {
    const goalKipsDTOs = State.ReadClass<DeepWorkKipsTodayState>(
        ESharedComponent.DeepWorkKipsToday
    ).state.kipsDTO.kipsByGoal;

    const goalKipsDTO = goalKipsDTOs.find((dto) => dto.goalName === goal.name);

    if (goalKipsDTO === undefined) {
        return false;
    }

    return goalKipsDTO.limit.isBreached;
}

function ContainsANumber(myString: string) {
    return /\d/.test(myString);
}
function IsASingleWord(entryText: string) {
    return entryText.split(" ").length === 1;
}
